import { Avatar, ScrollShadow } from '@heroui/react';
import { IAccessLevel } from '@quesmed/types/models';
import { useMemo } from 'react';
import { RiLogoutCircleRLine } from 'react-icons/ri';
import { Link } from 'react-router-dom';
import { getMenus, ROUTES } from '@/config';
import { useAuthHandler } from '@/core/hooks';
import { useAuthState, useGlobal } from '@/store';
import packageJson from '../../../package.json';
import { cn, Tooltip } from '../ui';
import { MenuItem } from './MenuItem';

export const SideBar = () => {
  const { logout } = useAuthHandler();
  const { tokenDecoded, isSuperAdmin } = useAuthState();
  const sidebarCollapsed = useGlobal.use.sidebarCollapsed();
  const environment = useGlobal.use.environment();
  const {
    firstName = 'Quesmed',
    lastName = '',
    accessLevel = IAccessLevel.EDITOR,
    username = '',
  } = tokenDecoded || {};

  const onLogout = () => {
    logout();
  };

  const items = useMemo(
    () => getMenus(accessLevel, isSuperAdmin, environment),
    [accessLevel, username, environment, isSuperAdmin],
  );

  return (
    <div
      className={cn(
        'bg-background h-screen flex-shrink-0 shadow-material-lg transition-all duration-300',
        sidebarCollapsed ? 'w-[5rem]' : 'w-[18rem] xl:w-[18rem] 2xl:w-[19rem]',
      )}
    >
      <Link
        to={ROUTES.MAIN}
        className={cn(
          'md:pt-8 flex items-center transition-all duration-300',
          sidebarCollapsed ? 'justify-center p-2' : 'space-x-3 p-5 md:px-6 ',
        )}
      >
        <div className='p-2 bg-primary-300/40 dark:bg-primary-300 rounded-md'>
          <img alt='logo' width={40} height={40} src='/logo.svg' className='transition-all duration-300' />
        </div>
        {!sidebarCollapsed && (
          <div className='flex flex-col'>
            <h5 className='text-foreground font-medium uppercase text-2xl'>Quesmed.</h5>
            <p className='text-foreground-700 ml-1 text-xs font-mono'>v{packageJson.version}</p>
          </div>
        )}
      </Link>

      <ScrollShadow
        hideScrollBar
        className={cn(
          'md:py-7 flex flex-col space-y-9 md:h-[calc(100vh-235px)] h-[calc(100vh-195px)] overflow-y-scroll transition-all duration-300',
          sidebarCollapsed ? 'p-2' : 'p-5 md:px-6',
        )}
      >
        {Object.entries(items).map(([key, menus]) => {
          return (
            <div className='flex flex-col space-y-2' key={key}>
              {!sidebarCollapsed && (
                <span className='font-semibold uppercase text-foreground-700 px-3'>{key}</span>
              )}
              <div className='flex flex-col'>
                {menus.map((menu) => (
                  <MenuItem
                    accessLevel={accessLevel}
                    level={0}
                    menu={menu}
                    key={menu.label}
                    collapsed={sidebarCollapsed}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </ScrollShadow>

      <div className={cn('transition-all duration-300', sidebarCollapsed ? 'p-2' : 'p-5 md:p-8')}>
        <div
          className={cn(
            'bg-primary-200 rounded-md py-4 flex flex-row items-center transition-all duration-300',
            sidebarCollapsed ? 'px-2 justify-center' : 'px-4 justify-between',
          )}
        >
          {!sidebarCollapsed && (
            <div className='flex flex-row space-x-3 lg:space-x-4'>
              <Avatar name={`${firstName[0]}${lastName[0]}`} />
              <div>
                <p className='text-sm lg:text-sm text-foreground font-semibold'>{firstName}</p>
                <p className='text-[10px] mt-1 lg:text-xs uppercase text-foreground'>{accessLevel}</p>
              </div>
            </div>
          )}
          <Tooltip content={'Logout'} placement={sidebarCollapsed ? 'right' : 'top'}>
            <button
              type='button'
              onClick={onLogout}
              className={cn(
                'flex justify-center items-center p-2 rounded-full w-10 h-10 hover:bg-primary-300',
                sidebarCollapsed && 'w-full',
              )}
            >
              <RiLogoutCircleRLine className='text-foreground w-5 h-5' />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
